<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { Icon } from '@oat-sa-private/ui-elements';

    /**
     * Details component - can expand to reveal additional slotted contents
     */

    /**
     * @typedef {Object} Props
     * @property {String} key
     * @property {Object} [config]
     * @property {Boolean} [config.collapsible]
     * @property {Boolean} [config.collapsed]
     * @property {Boolean} [collapsed]
     * @property {Boolean} [outlined]
     * @property {String} ariaLabelCollapse
     * @property {String} ariaLabelExpand
     * @property {import('svelte').Snippet} [summary]
     * @property {import('svelte').Snippet} [children]
     * @property {Function} onToggle
     */
    /** @type {Props} */
    let {
        key,
        config = { collapsible: true },
        collapsed = false,
        outlined = false,
        ariaLabelCollapse,
        ariaLabelExpand,
        summary,
        children,
        onToggle
    } = $props();

    let detailsElement = $state();

    let open = $derived(!(config.collapsed || collapsed));
    let summaryAriaLabel = $derived(open ? ariaLabelCollapse : ariaLabelExpand);
    let summaryIcon = $derived(open ? 'chevron-up-12' : 'chevron-down-12');

    /**
     * Handle the native toggle event of the <details>
     */
    function handleToggle() {
        open = detailsElement?.open;
        if (typeof onToggle === 'function') {
            onToggle({
                key,
                collapsed: !open
            });
        }
    }
</script>

<style>
    .a11y-group {
        color: var(--color-text-default);
        background: var(--color-bg-default);
        border: var(--border-thin) solid var(--color-gs-light-secondary); /* overridden below */
        border-radius: 3rem;
        padding-inline-start: 1.5rem;
        padding-inline-end: 2rem;

        &.outlined {
            outline: var(--border-medium) solid var(--color-border-selected);
            outline-offset: calc(-1 * var(--border-thin));
        }

        /* :global to use mixin without warning */
        & :global(summary),
        & :global(.summary) {
            color: var(--color-text-default);
            height: 5rem;
            list-style: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            margin-block: 0.5rem;
            margin-inline: -0.5rem;
            padding-inline: 0.5rem;
            @add-mixin outline-focus 0rem;

            & > :global(:first-child) {
                flex-grow: 1;
            }
        }
        & summary::-webkit-details-marker {
            display: none;
        }
    }
    .a11y-group :global(*) {
        box-sizing: border-box; /* fix a chrome bug where content-box is applied */
    }
</style>

{#if config.collapsible}
    <details class="a11y-group" class:outlined {open} bind:this={detailsElement} ontoggle={handleToggle}>
        <summary aria-label={summaryAriaLabel} title={summaryAriaLabel}>
            {@render summary?.()}
            <Icon name={summaryIcon} ariaHidden={true} />
        </summary>
        <!-- `hidden` is to solve `focusTrap` keyboard navigation -->
        <div class:hidden={!open}>
            {@render children?.()}
        </div>
    </details>
{:else}
    <div class="a11y-group" class:outlined>
        <div class="summary">
            {@render summary?.()}
        </div>
        {@render children?.()}
    </div>
{/if}
